// Spec: docs/plans/frontend-stack.md §3 (skin loader architecture).

import { Suspense, lazy, useMemo, type ComponentType } from 'react';
import { useSkin } from './lib/hooks/use-skin';

type SkinName = 'modern' | 'legacy' | 'deck';

const SKINS: Record<SkinName, () => Promise<{ default: ComponentType }>> = {
  modern: () => import('./skins/modern'),
  legacy: () => import('./skins/legacy'),
  deck: () => import('./skins/deck'),
};

/**
 * Landing paths for emailed links. These are pinned to `modern` regardless of
 * skin preference, and the set is asserted against the API's link builders by
 * scripts/__tests__/request-line-secrets.test.mjs.
 *
 * Two reasons, both load-bearing:
 *
 *  1. An emailed link is skin-agnostic — it is a cold navigation that may not
 *     even come from a logged-in browser. Only `modern` implements these
 *     pages; `deck` has a `$` catch-all that renders NotFoundPage. Before
 *     #1016 the email-change link went straight to the API and worked in any
 *     skin, so routing it through the SPA newly stranded deck users:
 *     `deck/game/settings-page.tsx` ships an EmailChangePanel, so they could
 *     start a change and never finish it, leaving `users.email_pending` set
 *     until staff intervened. `/activate` and `/password-reset/confirm` were
 *     already modern-only, so this also fixes a pre-existing deck breakage.
 *
 *  2. It makes a mid-flight skin swap impossible on exactly the pages that
 *     cannot survive one. `use-skin`'s tier-3 hydration resolves
 *     asynchronously from /api/auth/me, so a user with `users.skin = 'deck'`
 *     and no localStorage choice paints `modern` first and switches when the
 *     fetch lands. On a token page that unmounts the tree mid-request, and
 *     the token is in a fragment that has already been consumed — it is not
 *     recoverable by the remounting tree the way a query param was.
 */
const AUTH_LINK_PATHS: ReadonlySet<string> = new Set([
  '/activate',
  '/password-reset/confirm',
  '/email-change/verify',
  '/mail-suppression/confirm',
]);

function isAuthLinkPath(): boolean {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname.replace(/\/+$/, '');
  return AUTH_LINK_PATHS.has(path === '' ? '/' : path);
}

function BootSplash() {
  return (
    <div className="grid min-h-dvh place-items-center text-sm text-muted-foreground">
      Loading…
    </div>
  );
}

export function SkinLoader() {
  const preferred = useSkin();
  const skin = isAuthLinkPath() ? 'modern' : preferred;

  // Memoised on `skin`. `lazy()` returns a NEW component type on every call,
  // and a new type remounts the whole skin tree — so calling it inline made
  // any re-render of this component a full remount. Harmless when a remount
  // only re-read a query param; not harmless now that a page may hold a
  // single-use token captured from a fragment.
  const SkinApp = useMemo(() => lazy(SKINS[skin]), [skin]);

  return (
    <Suspense fallback={<BootSplash />}>
      <SkinApp />
    </Suspense>
  );
}
